import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateTime(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatRelativeTime(date: string | Date): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(d);
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  ms: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  ms: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= ms) {
      lastCall = now;
      fn(...args);
    }
  };
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    // Fix: replace base64url chars correctly (- → +, _ → /), then add padding
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  const exp = Number(payload.exp);
  if (Number.isNaN(exp)) return true;
  return Date.now() >= exp * 1000;
}

export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex <= 0) return '';
  return filename.slice(lastDotIndex + 1);
}

export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function validateEmail(email: string): boolean {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePhone(phone: string): boolean {
  const re = /^\+?[\d\s()-]{10,}$/;
  return re.test(phone);
}

export function getErrorMessage(error: unknown): string {
  // 1. Бэкенд отдаёт ошибки в формате ApiError { status, error, message, details }.
  //    У axios это лежит в error.response.data — достаём оттуда человекочитаемое сообщение.
  const apiError = (error as { response?: { data?: { message?: string; details?: string[]; error?: string } } })?.response?.data;
  if (apiError) {
    if (apiError.details && apiError.details.length > 0) return apiError.details.join('; ');
    if (apiError.message) return apiError.message;
    if (apiError.error) return apiError.error;
  }
  // 2. Обычные Error (например, сетевой сбой без ответа).
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unknown error occurred';
}

/**
 * Сообщение об ошибке для auth-форм (login/register) на русском с привязкой
 * к HTTP-статусу бэкенда. Раньше формы показывали сырой бэкенд-овый message
 * на английском («Account is not confirmed», «Email already exists»), что
 * сбивало пользователя: при 403 он не понимал, что нужно подтвердить email,
 * а при 409 — что аккаунт уже есть и надо войти/сбросить пароль.
 * <p>
 * Возвращает { title, hint } — title для toast.error, hint — короткая подсказка
 * (например, ссылка на сброс пароля), которую форма может показать отдельно.
 */
export function getAuthErrorMessage(error: unknown): { title: string; hint?: string } {
  const status = (error as { response?: { status?: number; data?: { error?: string; message?: string } } })?.response?.status;
  const apiErrorStr = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
    ?? (error as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? '';

  if (status === 403 && /not confirmed|не подтверждён|не подтвержден/i.test(apiErrorStr)) {
    return {
      title: 'Email не подтверждён',
      hint: 'Проверьте почту и перейдите по ссылке подтверждения. Если письма нет — запросите его повторно.',
    };
  }
  if (status === 409) {
    return {
      title: 'Аккаунт с таким email уже существует',
      hint: 'Войдите по паролю или восстановите доступ, если забыли его.',
    };
  }
  if (status === 401) {
    return { title: 'Неверный email или пароль' };
  }
  if (status === 429) {
    return { title: 'Слишком много попыток. Попробуйте позже.' };
  }
  // fallback на общий экстрактор
  return { title: getErrorMessage(error) };
}

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((result, item) => {
    const groupKey = String(item[key]);
    if (!result[groupKey]) result[groupKey] = [];
    result[groupKey].push(item);
    return result;
  }, {} as Record<string, T[]>);
}

export function sortBy<T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}